import { useState, useCallback, useEffect } from "react";

interface InfiniteScrollState {
  infiniteScrollEnabled: boolean;
  setInfiniteScrollEnabled: (enabled: boolean) => void;
}

/**
 * @deprecated Use `usePaginationMode` instead
 *
 * This hook is deprecated and will be removed in a future version.
 * Use `usePaginationMode` from `../usePaginationMode.ts` instead, which provides
 * unified pagination and infinite scroll state management.
 *
 * Migration guide:
 * ```
 * // Old way
 * const { infiniteScrollEnabled, setInfiniteScrollEnabled } = useInfiniteScrollState(entityName);
 *
 * // New way
 * const paginationMode = usePaginationMode(entityName);
 * // Access: paginationMode.paginationMode.mode === "infinite-scroll"
 * // Toggle: paginationMode.toggleInfiniteScroll(true/false)
 * ```
 *
 * Manages infinite scroll preference with localStorage persistence
 * Initializes from localStorage on mount and persists changes
 */
export function useInfiniteScrollState(entityName: string): InfiniteScrollState {
  const infiniteScrollStorageKey = `${entityName}-infinite-scroll`;
  const [infiniteScrollEnabled, setInfiniteScrollEnabledState] = useState(false);

  // Initialize infinite scroll preference from localStorage on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedInfiniteScroll = localStorage.getItem(infiniteScrollStorageKey);
      if (savedInfiniteScroll === "true") {
        setInfiniteScrollEnabledState(true);
      }
    }
  }, [infiniteScrollStorageKey]);

  // Persist infinite scroll preference to localStorage when it changes
  const setInfiniteScrollEnabled = useCallback(
    (enabled: boolean) => {
      setInfiniteScrollEnabledState(enabled);
      if (typeof window !== "undefined") {
        localStorage.setItem(infiniteScrollStorageKey, String(enabled));
      }
    },
    [infiniteScrollStorageKey]
  );

  return {
    infiniteScrollEnabled,
    setInfiniteScrollEnabled,
  };
}
